import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';

export type SvgRendererHandle = {
    zoomIn: () => void;
    zoomOut: () => void;
    resetZoom: () => void;
    copyToClipboard: () => Promise<void>;
};

type SvgRendererProps = {
    svg: string;
};

/**
 * Provides interactive pan, zoom, and clipboard features for inline SVG content.
 * @param svg - Serialized SVG markup to render within the component.
 * @param ref - Forwarded ref exposing zoom controls and clipboard helpers.
 * @returns A container hosting the rendered SVG with mouse-driven navigation support.
 */
export const SvgRenderer = forwardRef<SvgRendererHandle, SvgRendererProps>(
    ({ svg }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const svgRef = useRef<SVGSVGElement | null>(null);
        const gRef = useRef<SVGGElement | null>(null);

        // Pan & zoom state
        const state = useRef({
            scale: 1,
            translateX: 0,
            translateY: 0,
            isDragging: false,
            lastX: 0,
            lastY: 0,
        });

        /**
         * Applies the current scale and translation to the root group element.
         */
        const applyTransform = () => {
            const g = gRef.current;
            if (g) {
                g.setAttribute(
                    'transform',
                    `translate(${state.current.translateX}, ${state.current.translateY}) scale(${state.current.scale})`
                );
            }
        };

        /**
         * Updates the zoom factor and reapplies transforms based on the delta multiplier.
         * @param delta - Multiplicative change applied to the current zoom level.
         */
        const zoom = (delta: number) => {
            state.current.scale *= delta;
            applyTransform();
        };

        /**
         * Restores default zoom and translation values for the rendered SVG.
         */
        const resetZoom = () => {
            state.current.scale = 1;
            state.current.translateX = 0;
            state.current.translateY = 0;
            applyTransform();
        };

        useImperativeHandle(ref, () => ({
            zoomIn: () => zoom(1.1),
            zoomOut: () => zoom(0.9),
            resetZoom,
            copyToClipboard: copyToClipboard
        }));


        /**
         * Copies the current SVG rendering to the clipboard as a PNG blob.
         */
        const copyToClipboard = async () => {
            if (!svgRef.current) return;

            const svgElement = svgRef.current;
            const serializer = new XMLSerializer();
            const svgString = serializer.serializeToString(svgElement);

            const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });

            // Optional: convert to PNG (better for interoperability)
            const img = new Image();
            const url = URL.createObjectURL(svgBlob);
            img.src = url;

            await new Promise<void>((resolve) => {
                img.onload = () => resolve();
            });

            const canvas = document.createElement('canvas');
            canvas.width = svgElement.clientWidth || 800;
            canvas.height = svgElement.clientHeight || 600;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);

            const blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob((b) => resolve(b), 'image/png')
            );
            if (!blob) return;


            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob }),
            ]);


        }

        useEffect(() => {
            if (!containerRef.current) return;

            const parser = new DOMParser();
            const parsedSvg = parser.parseFromString(svg, 'image/svg+xml').documentElement;

            containerRef.current.innerHTML = '';

            const newSvg = parsedSvg.cloneNode(true) as SVGSVGElement;
            newSvg.style.width = '100%';
            newSvg.style.height = '100%';
            newSvg.style.cursor = 'grab';
            newSvg.style.userSelect = 'none';
            newSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            while (newSvg.firstChild) {
                g.appendChild(newSvg.firstChild);
            }
            newSvg.appendChild(g);

            svgRef.current = newSvg;
            gRef.current = g;

            containerRef.current.appendChild(newSvg);
            applyTransform();

            // Zoom with mouse wheel
            /**
             * Adjusts zoom level in response to mouse wheel interaction.
             * @param e - Wheel event emitted over the SVG element.
             */
            const handleWheel = (e: WheelEvent) => {
                e.preventDefault();
                const delta = e.deltaY < 0 ? 1.1 : 0.9;
                zoom(delta);
            };

            // Mouse drag pan
            /**
             * Starts the drag gesture for panning the SVG view.
             * @param e - Mouse event representing the initial press.
             */
            const handleMouseDown = (e: MouseEvent) => {
                state.current.isDragging = true;
                state.current.lastX = e.clientX;
                state.current.lastY = e.clientY;
                newSvg.style.cursor = 'grabbing';
            };

            /**
             * Updates the translation while the user drags the SVG around.
             * @param e - Mouse move event captured during the drag gesture.
             */
            const handleMouseMove = (e: MouseEvent) => {
                if (!state.current.isDragging) return;
                const dx = e.clientX - state.current.lastX;
                const dy = e.clientY - state.current.lastY;
                state.current.translateX += dx;
                state.current.translateY += dy;
                state.current.lastX = e.clientX;
                state.current.lastY = e.clientY;
                applyTransform();
            };

            /**
             * Ends the drag gesture and restores the default cursor.
             */
            const handleMouseUp = () => {
                state.current.isDragging = false;
                newSvg.style.cursor = 'grab';
            };

            newSvg.addEventListener('wheel', handleWheel);
            newSvg.addEventListener('mousedown', handleMouseDown);
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);

            return () => {
                newSvg.removeEventListener('wheel', handleWheel);
                newSvg.removeEventListener('mousedown', handleMouseDown);
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }, [svg]);

        return (
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    border: '1px solid #ccc',
                }}
            />
        );
    }
);
